<template>
    <div class="breakdown-chart">
        <div class="bc-title">{{ title }}</div>
        <a-spin :spinning="loading">
            <div class="bc-body" v-if="rows && rows.length > 0">
                <ECharts :option="option" class="bc-graph" />
            </div>
            <div class="bc-empty" v-else-if="!loading">
                {{ $t("metaInsights.noBreakdownData") }}
            </div>
        </a-spin>
    </div>
</template>

<script>
import Vue from "vue";
import _ from "lodash";
import { createComponent, plugin } from "echarts-for-vue";
import * as echarts from "echarts/lib/echarts";
import "echarts/lib/component/tooltip";
import "echarts/lib/component/legend";
import "echarts/lib/component/grid";
import { BarChart, PieChart } from "echarts/charts";
echarts.use([BarChart, PieChart]);
Vue.use(plugin, { echarts });

const NUMBER_FIELDS = ["spend", "impressions", "reach", "clicks", "ctr", "cpm", "cpc", "frequency"];

export default {
    name: "BreakdownChart",
    components: {
        ECharts: createComponent({ echarts }),
    },
    props: {
        title: { type: String, default: "" },
        rows: { type: Array, default: () => [] },
        breakdownKey: { type: String, required: true },
        metric: { type: String, default: "impressions" },
        chartType: { type: String, default: "bar" },
        loading: { type: Boolean, default: false },
        labelMap: { type: Object, default: () => ({}) },
        currency: { type: String, default: "" },
    },
    computed: {
        normalizedRows() {
            const key = this.breakdownKey;
            const metric = this.metric;
            const rows = this.rows || [];
            return rows
                .map((r) => {
                    const label = String(r[key] || "-");
                    const mapped = this.labelMap[label] || label;
                    return { label: mapped, value: Number(r[metric]) || 0 };
                })
                .sort((a, b) => b.value - a.value);
        },
        option() {
            const data = this.normalizedRows;
            if (this.chartType === "pie") {
                return {
                    tooltip: { trigger: "item" },
                    legend: { type: "scroll", bottom: 0 },
                    series: [
                        {
                            type: "pie",
                            radius: ["35%", "65%"],
                            center: ["50%", "45%"],
                            data: data.map((d) => ({ name: d.label, value: d.value })),
                            label: { formatter: "{b}: {d}%" },
                        },
                    ],
                };
            }
            return {
                tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
                grid: { left: "3%", right: "4%", bottom: "3%", containLabel: true },
                xAxis: { type: "value" },
                yAxis: {
                    type: "category",
                    data: data.map((d) => d.label),
                    axisLabel: { interval: 0 },
                    inverse: true,
                },
                series: [
                    {
                        type: "bar",
                        data: data.map((d) => d.value),
                        itemStyle: { color: "#1f6feb" },
                        barMaxWidth: 28,
                    },
                ],
            };
        },
    },
    methods: {
        sumOf(field) {
            return _.sumBy(this.rows || [], (r) => Number(r[field]) || 0);
        },
    },
};
</script>

<style lang="scss" scoped>
.breakdown-chart {
    display: flex;
    flex-direction: column;
    gap: 8px;
    background: var(--color-bg-default);
    border: 1px solid var(--color-border-default);
    border-radius: 8px;
    padding: 14px;
    min-height: 280px;

    .bc-title {
        font-size: 13px;
        font-weight: 700;
        color: var(--color-text-strong-default);
        text-transform: uppercase;
        letter-spacing: 0.4px;
    }

    .bc-body {
        height: 320px;
    }

    .bc-graph {
        width: 100%;
        height: 100%;
    }

    .bc-empty {
        padding: 30px;
        text-align: center;
        color: var(--color-text-muted-default);
    }
}
</style>
